// Drawola Studio — the app's home view. Fork-only.
//
// Phase 1: browse / create / manage every draw held by the multi-file store
// (excalidraw-app/storage). Opening a draw hands the doc id back to App.tsx,
// which drives `useMultiFileStore.switchTo()` (flush pending save -> swap
// scene) and flips the view to the editor.
//
// Rendering notes:
//  - The root carries upstream's `.excalidraw` class purely to inherit the
//    design tokens (`--color-primary`, `--island-bg-color`, …), which
//    `packages/excalidraw/css/theme.scss` scopes to that selector, plus
//    `theme--dark` for the dark palette. It is a SIBLING of the editor's
//    `.excalidraw-container`, never a descendant.
//  - It renders inside the app jotai `Provider` but OUTSIDE `<Excalidraw>`,
//    so i18n goes through `useStudioT()` (see ./i18n.ts) and dialogs are
//    local rather than upstream's editor-scoped `<Dialog>`.

import { THEME } from "@excalidraw/common";
// The Studio's own `t` (useStudioT) reads the app's locale files, so upstream
// keys are unreachable through it. Imported directly for the share action,
// which deliberately reuses an upstream string instead of adding a fork key.
import { t as upstreamT } from "@excalidraw/excalidraw/i18n";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Theme } from "@excalidraw/element/types";

import { analytics } from "../analytics/posthog";
import { PrivacyNotice } from "../analytics/PrivacyNotice";

import { AppLanguageSelect } from "../app-language/AppLanguageSelect";
import { SyncStatusIcon } from "../cloud/sync";
import { DRAWOLA_FEATURES } from "../drawola-features";
import {
  createDoc,
  deleteDoc,
  getActiveDocId,
  getDoc,
  listDocs,
  renameDoc,
  setActiveDocId,
} from "../storage/multiFileStore";
import { canWrite, formatMB, getSnapshot } from "../storage/quota";
import { startThumbnailBackfill } from "../storage/thumbnails";
import { LIMITS } from "../storage/types";
import { StorageMeterView } from "../storage/ui/StorageMeter";
import { estimateDocBytes } from "../storage/usage";
import { collaborationGuideHref } from "../manual/guides";

import { exportDocToFile } from "./exportDoc";
import { formatRelativeTime } from "./format";
import { useShownLanguage, useStudioT } from "./i18n";
import {
  IMPORT_ACCEPT,
  docNameFromFileName,
  parseImportedFile,
} from "./importDoc";
import {
  loadLayout,
  loadSort,
  saveHasDraws,
  saveLayout,
  saveSort,
  type StudioLayout,
  type StudioSort,
} from "./prefs";
import { filterDocs } from "./search";
import { hasStaticWelcome, removeStaticWelcome } from "./staticWelcome";
import { StudioCardMenu } from "./StudioCardMenu";
import { StudioConfirmDialog } from "./StudioConfirmDialog";
import { resolvePageMode, resolveStudioMode } from "./studioMode";
import { StudioSharesDialog } from "./StudioSharesDialog";
import { StudioWelcome } from "./StudioWelcome";
import { STUDIO_TEMPLATES, useTemplatePreviews } from "./templates";
import { welcomeAcknowledgment as pageWelcomeAcknowledgment } from "./welcomeAcknowledgment";

import type { StudioTranslate } from "./i18n";
import type { DocMatch } from "./search";
import type { StudioTemplate } from "./templates";
import type { WelcomeAcknowledgment } from "./welcomeAcknowledgment";
import type { CloudSyncApi } from "../cloud/sync";
import type { DocListEntry } from "../storage/multiFileStore";
import type {
  CanWriteResult,
  DocMeta,
  StorageSnapshot,
} from "../storage/types";

type Props = {
  /** Current app theme, so the Studio matches the editor in light and dark. */
  theme: Theme;
  /** Switch the editor to `docId` and show it. */
  onOpenDoc: (docId: string) => void | Promise<void>;
  /** Show the editor on a doc the Studio just created (already made active). */
  onOpenCreatedDoc: (docId: string) => void | Promise<void>;
  /** Called after the currently-active doc is deleted, so the editor resets. */
  onActiveDocDeleted: () => void | Promise<void>;
  /**
   * Upload `docId` to the share backend and show the generated link.
   *
   * Owned by App.tsx on purpose: the upload, the link dialog and the error
   * copy stay in one place, shared with the editor's own share action. Absent
   * when `SHOW_SHARE_LINK` is off, which hides the menu entry.
   */
  onShareDoc?: (docId: string) => void | Promise<void>;
  /**
   * Revoke one shareable link of a draw, from the manage-links dialog.
   *
   * Owned by App.tsx on purpose, like `onShareDoc`: the server call, the local
   * record removal and the error copy stay in one place. Absent when
   * `SHOW_SHARE_LINK` is off, which hides the menu entry.
   *
   * Resolves `true` when the share is gone, `false` when the revoke failed.
   * `silent` suppresses the app's error dialog, for the delete flow which
   * reports its own summary.
   */
  onRevokeShare?: (
    docId: string,
    shareId: string,
    silent?: boolean,
  ) => Promise<boolean>;
  /**
   * `useMultiFileStore().booted`. Until the store has finished migrating and
   * priming the scene with the active doc it would clobber whatever we hand
   * the editor, so opening/creating stays disabled for that brief window.
   */
  ready: boolean;
  /** Bump to force a re-read of the doc list. */
  refreshToken?: number;
  /** Cloud sync state and actions; absent when cloud storage is off. */
  cloudSync?: CloudSyncApi;
  /** Where the welcome CTA click is stored; the page-wide cookie by default. */
  welcomeAcknowledgment?: WelcomeAcknowledgment;
};

const GridIcon = (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M3 3h6v6H3V3Zm8 0h6v6h-6V3ZM3 11h6v6H3v-6Zm8 0h6v6h-6v-6Z"
    />
  </svg>
);

const ListIcon = (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M3 4h14v2.5H3V4Zm0 4.75h14v2.5H3v-2.5ZM3 13.5h14V16H3v-2.5Z"
    />
  </svg>
);

const SearchIcon = (
  <svg
    viewBox="0 0 20 20"
    width="16"
    height="16"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
  >
    <circle cx="9" cy="9" r="5.25" />
    <path d="m13 13 3.5 3.5" />
  </svg>
);

const ImportIcon = (
  <svg
    viewBox="0 0 20 20"
    width="15"
    height="15"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 12.75V3.5" />
    <path d="m6.5 7 3.5-3.5L13.5 7" />
    <path d="M3.75 13v2.25a1.25 1.25 0 0 0 1.25 1.25h10a1.25 1.25 0 0 0 1.25-1.25V13" />
  </svg>
);

const SketchIcon = (
  <svg
    viewBox="0 0 48 48"
    width="40"
    height="40"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="7" y="9" width="20" height="15" rx="2" />
    <circle cx="33" cy="31" r="7" />
    <path d="M13 39h13" />
  </svg>
);

/**
 * Reuse the multi-file store's existing `quota.*` strings so a blocked write
 * reads identically here and in `BlockingQuotaModal`.
 */
const describeQuotaError = (
  result: Exclude<CanWriteResult, { ok: true }>,
  t: StudioTranslate,
): string => {
  switch (result.reason) {
    case "doc-count":
      return t("quota.detail.docCount", {
        max: result.meta.max ?? LIMITS.MAX_DOCUMENTS,
      });
    case "doc-size":
      return t("quota.detail.docSize", {
        size: formatMB(result.meta.size ?? 0),
      });
    case "policy-total":
      return t("quota.detail.policyTotal");
    case "quota":
    default:
      return t("quota.detail.quota", {
        quota: formatMB(result.meta.quota ?? 0),
        usage: formatMB(result.meta.usage ?? 0),
      });
  }
};

export const DrawolaStudio: React.FC<Props> = ({
  theme,
  onOpenDoc,
  onOpenCreatedDoc,
  onActiveDocDeleted,
  onShareDoc,
  onRevokeShare,
  ready,
  refreshToken,
  cloudSync,
  welcomeAcknowledgment = pageWelcomeAcknowledgment,
}) => {
  const t = useStudioT();
  const shownLanguage = useShownLanguage();
  const guideHref = collaborationGuideHref(shownLanguage);

  const [docs, setDocs] = useState<DocListEntry[] | null>(null);
  // The `[]` fallback after a failed listing is not an empty library, so the
  // welcome CTA and the has-draws hint must not read it as one.
  const [listFailed, setListFailed] = useState(false);
  // Read once: only this component and App.tsx remove the node, and a Studio
  // mounted after it is gone shows its own loading state instead of nothing.
  const [awaitingHandover] = useState(hasStaticWelcome);
  // Read once per mount and changed only by a CTA click, so listings, Drive
  // auth changes and deletes never dismiss the welcome or renew the cookie.
  const [acknowledged, setAcknowledged] = useState(() =>
    welcomeAcknowledgment.isAcknowledged(),
  );
  const [query, setQuery] = useState("");
  const [layout, setLayout] = useState<StudioLayout>(loadLayout);
  const [sort, setSort] = useState<StudioSort>(loadSort);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DocMeta | null>(null);
  const [deleteCloudCopy, setDeleteCloudCopy] = useState(false);
  const [sharesDocId, setSharesDocId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [storage, setStorage] = useState<StorageSnapshot | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Set by the welcome CTA when it hands the page to the Studio. The focused
  // CTA unmounts there, and focus left on <body> would send the next key press
  // to the hidden editor's document-level shortcuts.
  const focusSearchOnStudioRef = useRef(false);

  // Rendered lazily at idle priority and memoised module-side — see
  // ./templates/preview.ts. Gated on `ready` for the same reason the
  // thumbnail backfill is: keep the store's boot path to itself.
  const templatePreviews = useTemplatePreviews(STUDIO_TEMPLATES, ready);

  const refresh = useCallback(async () => {
    try {
      setDocs(await listDocs());
      setListFailed(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[drawOla] studio: listing docs failed", e);
      setDocs([]);
      setListFailed(true);
      setError(t("studio.errors.listFailed"));
    }
    // Deliberately a second try/catch, not one `Promise.all`: a failing
    // storage estimate must never cost us the document list. The meter simply
    // keeps its last value (or its loading state on first paint).
    try {
      setStorage(await getSnapshot());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[drawOla] studio: storage snapshot failed", e);
    }
  }, [t]);

  // List only once the store has booted: before the boot migration runs,
  // `listDocs` can answer `[]` for a library that is about to hold the
  // starter or migrated doc, and nothing else would list again after it. Until
  // then `docs` stays null, so the static welcome stays up with its disabled
  // CTA and no flag is written. A later not-ready spell keeps the last list.
  useEffect(() => {
    if (ready) {
      refresh();
    }
  }, [ready, refresh, refreshToken]);

  // Draws made before thumbnails existed carry no preview. Fill them in while
  // the Studio is on screen: one doc at a time, at idle priority, refreshing
  // the list after each so cards populate progressively. Cancelled on unmount,
  // which is exactly when the app flips back to the editor.
  useEffect(() => {
    if (!ready) {
      return undefined;
    }
    const backfill = startThumbnailBackfill({
      onDocUpdated: () => {
        refresh();
      },
    });
    return () => backfill.cancel();
  }, [ready, refresh]);

  const closeMenu = useCallback(() => setOpenMenuId(null), []);

  // close the "⋮" popover on an outside click (Escape is handled by the
  // root's React `onKeyDown`, which also stops propagation to the editor).
  //
  // This listener reads the DOM tree, not the React tree, so it has to know
  // about the portal too: the menu itself now lives in `document.body` under
  // `.drawola-studio-portal` (see StudioCardMenu.tsx) and is therefore NOT a
  // descendant of `.drawola-studio__menu-wrap` any more. Without the second
  // selector every click on a menu item would close the menu before the
  // item's own `click` handler ran.
  useEffect(() => {
    if (!openMenuId) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        !target?.closest(".drawola-studio__menu-wrap, .drawola-studio-portal")
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openMenuId]);

  const visibleDocs = useMemo(() => {
    if (!docs) {
      return null;
    }
    // Name AND scene text; see ./search.ts. A content match carries the
    // snippet the card renders under the name.
    const sorted = [...filterDocs(docs, query)];
    if (sort === "name") {
      sorted.sort((a, b) => a.doc.name.localeCompare(b.doc.name));
    } else if (sort === "created") {
      sorted.sort((a, b) => b.doc.createdAt - a.doc.createdAt);
    } else {
      sorted.sort((a, b) => b.doc.updatedAt - a.doc.updatedAt);
    }
    return sorted;
  }, [docs, query, sort]);

  // The doc whose manage-links dialog is open, re-read from the live list so a
  // revoke (which bumps `refreshToken` and re-lists) shows the updated shares.
  const sharesDoc = sharesDocId
    ? docs?.find((d) => d.id === sharesDocId) ?? null
    : null;

  const hasLiveShare = (doc: DocMeta) =>
    (doc.shares ?? []).some((share) => share.expiresAt > Date.now());

  const changeLayout = useCallback((next: StudioLayout) => {
    setLayout(next);
    saveLayout(next);
  }, []);

  const changeSort = useCallback((next: StudioSort) => {
    setSort(next);
    saveSort(next);
  }, []);

  // -------------------------------------------------------------------------
  // actions
  // -------------------------------------------------------------------------

  const handleCreate = useCallback(async () => {
    if (busy || !ready) {
      return;
    }
    const attempt = analytics.start("studio_create");
    setBusy(true);
    setError(null);
    try {
      const check = await canWrite(0);
      if (!check.ok) {
        attempt.reject("storage_quota");
        setError(describeQuotaError(check, t));
        return;
      }
      const name = `${t(
        "files.untitledPrefix",
      )} ${new Date().toLocaleString()}`;
      const meta = await createDoc(name);
      attempt.succeed();
      await setActiveDocId(meta.id);
      await onOpenCreatedDoc(meta.id);
    } catch (e) {
      attempt.fail(e);
      // eslint-disable-next-line no-console
      console.error("[drawOla] studio: create failed", e);
      setError(t("studio.errors.createFailed"));
    } finally {
      setBusy(false);
    }
  }, [busy, onOpenCreatedDoc, ready, t]);

  /**
   * Create a draw pre-filled with a built-in template, then open it.
   *
   * Mirrors `handleCreate`'s pre-flight exactly (busy/ready guard, `canWrite`
   * first with the same `describeQuotaError` message, `studio.errors.
   * createFailed` on anything else) with one measured difference: the size
   * handed to `canWrite` is the template's real estimate rather than `0`,
   * because a seeded doc is not empty. That is the same call `handleDuplicate`
   * makes for the doc it is about to copy.
   *
   * It also opens through `onOpenDoc`, NOT `onOpenCreatedDoc`. Both end up
   * showing the editor, but `onOpenCreatedDoc` is `useMultiFileStore.
   * onCreated()`, which resets the canvas to an empty scene — correct for a
   * blank draw, fatal here: the template would be wiped off the canvas and the
   * next debounced save would write the empty scene back over it. `onOpenDoc`
   * is `switchTo()`, which flushes the previous draw's pending save, marks the
   * new doc active and loads its stored elements — i.e. the template.
   */
  const handleCreateFromTemplate = useCallback(
    async (template: StudioTemplate) => {
      if (busy || !ready) {
        return;
      }
      const attempt = analytics.start("studio_template");
      setBusy(true);
      setError(null);
      try {
        const scene = template.build();
        const check = await canWrite(
          estimateDocBytes(scene.elements, scene.appState, {}),
        );
        if (!check.ok) {
          attempt.reject("storage_quota");
          setError(describeQuotaError(check, t));
          return;
        }
        const meta = await createDoc(t(template.nameKey), {
          elements: scene.elements,
          appState: scene.appState,
        });
        attempt.succeed();
        await onOpenDoc(meta.id);
      } catch (e) {
        attempt.fail(e);
        // eslint-disable-next-line no-console
        console.error("[drawOla] studio: template create failed", e);
        setError(t("studio.errors.createFailed"));
      } finally {
        setBusy(false);
      }
    },
    [busy, onOpenDoc, ready, t],
  );

  /**
   * Import a picked `.drawola` / `.excalidraw` / `.json` scene as a new draw,
   * then open it.
   *
   * Pre-flight is `handleDuplicate`'s, verbatim: `canWrite` with the real
   * estimated size and NO `docId`, so the document cap is enforced alongside
   * the per-doc and total-size policies, and a refusal is rendered through the
   * shared `describeQuotaError` copy. Anything else — an unreadable file, a
   * library file, a truncated JSON — surfaces as the generic import error;
   * `parseImportedFile` never returns a half-valid scene, so there is no
   * partial-import state to clean up.
   *
   * Opens via `onOpenDoc` (= `useMultiFileStore.switchTo`), NOT
   * `onOpenCreatedDoc` (= `onCreated`), for the same reason the template path
   * does: `onCreated` resets the canvas to an empty scene, and the next
   * debounced save would write that emptiness back over the imported doc.
   */
  const handleImportFile = useCallback(
    async (file: File | null) => {
      if (!file || busy || !ready) {
        return;
      }
      const attempt = analytics.start("studio_import");
      setBusy(true);
      setError(null);
      try {
        const scene = await parseImportedFile(file);
        const check = await canWrite(
          estimateDocBytes(scene.elements, scene.appState, scene.files),
        );
        if (!check.ok) {
          attempt.reject("storage_quota");
          setError(describeQuotaError(check, t));
          return;
        }
        const meta = await createDoc(
          docNameFromFileName(file.name, t("files.untitledPrefix")),
          {
            elements: scene.elements,
            appState: scene.appState,
            files: scene.files,
          },
        );
        attempt.succeed();
        await onOpenDoc(meta.id);
      } catch (e) {
        attempt.fail(e);
        // eslint-disable-next-line no-console
        console.error("[drawOla] studio: import failed", e);
        setError(t("studio.import.failed"));
      } finally {
        setBusy(false);
      }
    },
    [busy, onOpenDoc, ready, t],
  );

  const handleOpen = useCallback(
    async (docId: string) => {
      setOpenMenuId(null);
      setError(null);
      if (!ready) {
        return;
      }
      try {
        await onOpenDoc(docId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] studio: open failed", e);
        setError(t("studio.errors.openFailed"));
      }
    },
    [onOpenDoc, ready, t],
  );

  const handleRenameCommit = useCallback(
    async (doc: DocMeta, nextName: string) => {
      setRenamingId(null);
      const trimmed = nextName.trim();
      if (!trimmed || trimmed === doc.name) {
        return;
      }
      try {
        await renameDoc(doc.id, trimmed);
        await refresh();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] studio: rename failed", e);
        setError(t("studio.errors.renameFailed"));
      }
    },
    [refresh, t],
  );

  const handleDuplicate = useCallback(
    async (docId: string) => {
      setOpenMenuId(null);
      setError(null);
      if (busy) {
        return;
      }
      setBusy(true);
      try {
        const source = await getDoc(docId);
        if (!source) {
          await refresh();
          return;
        }
        // no `docId` argument -> `canWrite` also enforces the document cap
        const check = await canWrite(source.sizeBytes ?? 0);
        if (!check.ok) {
          setError(describeQuotaError(check, t));
          return;
        }
        await createDoc(t("studio.copyName", { name: source.name }), {
          elements: source.elements,
          appState: source.appState,
          files: source.files,
        });
        await refresh();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] studio: duplicate failed", e);
        setError(t("studio.errors.duplicateFailed"));
      } finally {
        setBusy(false);
      }
    },
    [busy, refresh, t],
  );

  const handleExport = useCallback(
    async (docId: string) => {
      setOpenMenuId(null);
      setError(null);
      const attempt = analytics.start("studio_export_json");
      try {
        const ok = await exportDocToFile(docId, t("files.untitledPrefix"));
        if (ok) {
          attempt.succeed("download_handoff");
        } else {
          attempt.reject("document_missing");
        }
        if (!ok) {
          await refresh();
        }
      } catch (e) {
        attempt.fail(e);
        // eslint-disable-next-line no-console
        console.error("[drawOla] studio: export failed", e);
        setError(t("studio.errors.exportFailed"));
      }
    },
    [refresh, t],
  );

  /**
   * Hand a stored draw to App.tsx so it can be shared as a link.
   *
   * Guarded by `busy` because the upload is a network round trip and a second
   * click would send the scene twice. No `ready` guard, unlike the actions
   * that open or seed a draw: this one only reads the store and never touches
   * the editor scene, so the store's boot has nothing to clobber. No local
   * error state either: `onShareDoc` never throws, it renders the failure in
   * the app's own error dialog next to the editor's share action.
   */
  const handleShare = useCallback(
    async (docId: string) => {
      setOpenMenuId(null);
      setError(null);
      if (!onShareDoc || busy) {
        return;
      }
      setBusy(true);
      try {
        await onShareDoc(docId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] studio: share failed", e);
      } finally {
        setBusy(false);
      }
    },
    [busy, onShareDoc],
  );

  const handleDeleteConfirmed = useCallback(async () => {
    const doc = pendingDelete;
    setPendingDelete(null);
    if (!doc) {
      return;
    }
    setError(null);
    // Cloud copy first, best effort, only when asked. The local delete must
    // happen even when this fails, so the failure is reported afterwards.
    let remoteFailed = false;
    if (deleteCloudCopy && cloudSync && doc.cloud) {
      remoteFailed = !(await cloudSync.engine.deleteRemote(doc.id));
    }
    // Revoke every share first, best effort. The local delete must happen even
    // when a revoke fails, so failures are collected and reported afterwards.
    let failedRevokes = 0;
    if (onRevokeShare) {
      for (const share of doc.shares ?? []) {
        try {
          const ok = await onRevokeShare(doc.id, share.id, true);
          if (!ok) {
            failedRevokes += 1;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            "[drawOla] studio: revoking a share on delete failed",
            e,
          );
          failedRevokes += 1;
        }
      }
    }
    try {
      const wasActive = doc.id === (await getActiveDocId());
      await deleteDoc(doc.id);
      if (wasActive) {
        await onActiveDocDeleted();
      }
      await refresh();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[drawOla] studio: delete failed", e);
      setError(t("studio.errors.deleteFailed"));
      return;
    }
    if (failedRevokes > 0) {
      setError(t("studio.delete.revokeFailed"));
    }
    if (remoteFailed && cloudSync) {
      setError(
        t("cloud.sync.deleteRemoteFailed", {
          provider: cloudSync.providerName(doc),
        }),
      );
    }
  }, [
    cloudSync,
    deleteCloudCopy,
    onActiveDocDeleted,
    onRevokeShare,
    pendingDelete,
    refresh,
    t,
  ]);

  // -------------------------------------------------------------------------
  // rendering
  // -------------------------------------------------------------------------

  // A local-only draw has no binding, so its tooltip names the provider a
  // backup would go to. Ask the engine which provider this build ships rather
  // than hardcoding one; `undefined` when cloud storage is off.
  const firstProviderId = cloudSync?.engine.firstProviderId() ?? null;
  const defaultProviderName =
    cloudSync && firstProviderId
      ? cloudSync.engine.providerName(firstProviderId)
      : undefined;

  // Read on every render: `cloudSync` changes on each engine event, auth
  // included, so a Drive sign-in or sign-out updates the CTA outcome and the
  // has-draws hint without a new listing.
  const driveConnected =
    !!cloudSync &&
    !!firstProviderId &&
    cloudSync.engine.isSignedIn(firstProviderId);
  // Content only steers the CTA and the has-draws hint, never the page. A
  // failed listing is unknown data, not an empty library.
  const contentMode =
    docs === null || listFailed
      ? "studio"
      : resolveStudioMode({ docs, driveConnected });
  // Only "/" is a page; other paths keep the Studio, as the static gate in
  // index.html already assumes when it hides the welcome there.
  const mode = resolvePageMode({
    isRoot: window.location.pathname === "/",
    acknowledged,
  });
  const listed = docs !== null && !listFailed;

  /**
   * The welcome CTA. Acknowledges first, then creates a draw only for a
   * library a successful listing proved empty; existing work, a Drive session
   * or a failed listing just show the Studio. For the empty case the welcome
   * stays up while the draw is made, so the Studio does not flash before the
   * editor; a failed creation then shows its error in the Studio.
   */
  const handleContinue = async () => {
    if (busy || !ready || docs === null) {
      return;
    }
    welcomeAcknowledgment.acknowledge();
    if (listed && contentMode === "welcome") {
      await handleCreate();
    }
    focusSearchOnStudioRef.current = true;
    setAcknowledged(true);
  };

  // Swap the static welcome for this component before the browser paints, so
  // the visitor never sees both or neither. Removal is idempotent, which keeps
  // StrictMode's second effect run harmless.
  const settled = docs !== null;
  useLayoutEffect(() => {
    if (settled) {
      removeStaticWelcome();
    }
  }, [settled]);

  // Runs before paint, so no key press can land on <body> in between. After a
  // successful create the Studio is already unmounted and this never runs.
  useLayoutEffect(() => {
    if (mode === "studio" && focusSearchOnStudioRef.current) {
      focusSearchOnStudioRef.current = false;
      searchInputRef.current?.focus();
    }
  }, [mode]);

  // The has-draws hint is written from a listing that worked and kept as it
  // was after a failed one. It follows the content, not the current path, and
  // it never decides the welcome.
  useEffect(() => {
    if (listed) {
      saveHasDraws(contentMode === "studio");
    }
  }, [listed, contentMode]);

  // The dropdown half of this renders through a portal — see
  // StudioCardMenu.tsx for why (the cards clip their overflow) and for how the
  // portal keeps the theme tokens. Both layouts below use it unchanged.
  const renderMenu = (doc: DocMeta) => (
    <StudioCardMenu
      theme={theme}
      open={openMenuId === doc.id}
      label={t("studio.menu.aria", { name: doc.name })}
      onToggle={() =>
        setOpenMenuId((current) => (current === doc.id ? null : doc.id))
      }
      onClose={closeMenu}
    >
      <button
        type="button"
        role="menuitem"
        onClick={(event) => {
          event.stopPropagation();
          handleOpen(doc.id);
        }}
      >
        {t("studio.menu.open")}
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={(event) => {
          event.stopPropagation();
          setOpenMenuId(null);
          setRenamingId(doc.id);
        }}
      >
        {t("studio.menu.rename")}
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={(event) => {
          event.stopPropagation();
          handleDuplicate(doc.id);
        }}
      >
        {t("studio.menu.duplicate")}
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={(event) => {
          event.stopPropagation();
          handleExport(doc.id);
        }}
      >
        {t("studio.menu.export")}
      </button>
      {DRAWOLA_FEATURES.SHOW_SHARE_LINK && onShareDoc && (
        <button
          type="button"
          role="menuitem"
          onClick={(event) => {
            event.stopPropagation();
            handleShare(doc.id);
          }}
        >
          {upstreamT("buttons.getShareableLink")}
        </button>
      )}
      {DRAWOLA_FEATURES.SHOW_SHARE_LINK &&
        onRevokeShare &&
        (doc.shares?.length ?? 0) > 0 && (
          <button
            type="button"
            role="menuitem"
            onClick={(event) => {
              event.stopPropagation();
              setOpenMenuId(null);
              setSharesDocId(doc.id);
            }}
          >
            {t("studio.menu.shares")}
          </button>
        )}
      <button
        type="button"
        role="menuitem"
        className="drawola-studio__menu-item--danger"
        onClick={(event) => {
          event.stopPropagation();
          setOpenMenuId(null);
          setDeleteCloudCopy(false);
          setPendingDelete(doc);
        }}
      >
        {t("studio.menu.delete")}
      </button>
    </StudioCardMenu>
  );

  const renderName = (doc: DocMeta) =>
    renamingId === doc.id ? (
      <input
        className="drawola-studio__rename-input"
        defaultValue={doc.name}
        autoFocus
        aria-label={t("studio.menu.rename")}
        onFocus={(event) => event.currentTarget.select()}
        onClick={(event) => event.stopPropagation()}
        onBlur={(event) => handleRenameCommit(doc, event.currentTarget.value)}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") {
            handleRenameCommit(doc, event.currentTarget.value);
          } else if (event.key === "Escape") {
            setRenamingId(null);
          }
        }}
      />
    ) : (
      // a real <button> rather than a clickable <span>, so the name is
      // keyboard-reachable in both layouts
      <button
        type="button"
        className="drawola-studio__doc-name"
        title={doc.name}
        onClick={() => handleOpen(doc.id)}
      >
        {doc.name}
      </button>
    );

  // Why this draw is in the filtered list: the piece of scene text that
  // matched, with the hit highlighted. Absent when the name itself matched:
  // the name is right above, so repeating it would say nothing. User content,
  // so nothing here is translated.
  const renderSnippet = (snippet: DocMatch["snippet"]) =>
    snippet ? (
      <span className="drawola-studio__snippet" title={snippet.text}>
        {snippet.text.slice(0, snippet.hitStart)}
        <mark className="drawola-studio__snippet-hit">
          {snippet.text.slice(snippet.hitStart, snippet.hitEnd)}
        </mark>
        {snippet.text.slice(snippet.hitEnd)}
      </span>
    ) : null;

  const renderThumbnail = (doc: DocMeta) =>
    doc.thumbnail ? (
      <img
        className="drawola-studio__thumb-img"
        src={doc.thumbnail}
        alt=""
        loading="lazy"
      />
    ) : (
      // No preview yet: an empty draw, or one the backfill has not reached.
      <span className="drawola-studio__thumb-placeholder" aria-hidden="true">
        {SketchIcon}
      </span>
    );

  const emptyLibrary = visibleDocs !== null && (docs?.length ?? 0) === 0;
  const noSearchResults =
    visibleDocs !== null && visibleDocs.length === 0 && !emptyLibrary;

  // The static welcome stays alone on screen until the first listing settles.
  if (docs === null && awaitingHandover) {
    return null;
  }

  const rootClassName = clsx("excalidraw", "drawola-studio", {
    "theme--dark": theme === THEME.DARK,
  });

  // The editor keeps `handleKeyboardGlobally` listeners on `document`.
  // React attaches its own listeners on the app root, which sits below
  // `document`, so stopping propagation here keeps Studio keystrokes out
  // of the canvas shortcut handler.
  /** Keeps Studio key presses away from the editor and closes menus on Escape. */
  const handleRootKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      setOpenMenuId(null);
    }
  };

  const errorAlert = error && (
    <div className="drawola-studio__error" role="alert">
      <span>{error}</span>
      <button
        type="button"
        aria-label={t("studio.dismiss")}
        onClick={() => setError(null)}
      >
        ✕
      </button>
    </div>
  );

  // Full screen with no top bar, the same box as the static block, so the
  // handover does not move anything.
  if (mode === "welcome") {
    return (
      <div className={rootClassName} onKeyDown={handleRootKeyDown}>
        <div className="drawola-studio__scroll">
          {errorAlert}
          <StudioWelcome
            onContinue={handleContinue}
            disabled={busy || !ready || docs === null}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={rootClassName} onKeyDown={handleRootKeyDown}>
      <div className="drawola-studio__scroll">
        <header className="drawola-studio__topbar">
          <div className="drawola-studio__brand">
            <img
              className="drawola-studio__logo"
              src="/favicon.svg"
              alt=""
              width={32}
              height={32}
            />
            <span className="drawola-studio__wordmark">
              {t("studio.title")}
            </span>
          </div>

          {/* The controls row NEVER wraps (see studio.scss): the search field
              is the only flexible item, everything else is `flex: none`. The
              meter lives inside this row too, so it can never be split off
              onto a line of its own. */}
          <div className="drawola-studio__controls">
            {/* Hidden below 900px (see studio.scss) — the create strip and the
                draw grid are what a narrow screen needs the width for. */}
            <div className="drawola-studio__meter">
              <StorageMeterView snapshot={storage} t={t} variant="compact" />
            </div>

            <label className="drawola-studio__search">
              <span className="drawola-studio__search-icon" aria-hidden="true">
                {SearchIcon}
              </span>
              <input
                ref={searchInputRef}
                type="search"
                value={query}
                placeholder={t("studio.searchPlaceholder")}
                aria-label={t("studio.searchPlaceholder")}
                onChange={(event) => setQuery(event.currentTarget.value)}
              />
            </label>

            <label className="drawola-studio__sort">
              <span className="drawola-studio__visually-hidden">
                {t("studio.sort.label")}
              </span>
              <select
                value={sort}
                aria-label={t("studio.sort.label")}
                onChange={(event) =>
                  changeSort(event.currentTarget.value as StudioSort)
                }
              >
                <option value="updated">{t("studio.sort.updated")}</option>
                <option value="name">{t("studio.sort.name")}</option>
                <option value="created">{t("studio.sort.created")}</option>
              </select>
            </label>

            <div
              className="drawola-studio__layout-toggle"
              role="group"
              aria-label={t("studio.layout.label")}
            >
              <button
                type="button"
                className={clsx({ "is-active": layout === "grid" })}
                aria-pressed={layout === "grid"}
                title={t("studio.layout.grid")}
                aria-label={t("studio.layout.grid")}
                onClick={() => changeLayout("grid")}
              >
                {GridIcon}
              </button>
              <button
                type="button"
                className={clsx({ "is-active": layout === "list" })}
                aria-pressed={layout === "list"}
                title={t("studio.layout.list")}
                aria-label={t("studio.layout.list")}
                onClick={() => changeLayout("list")}
              >
                {ListIcon}
              </button>
            </div>

            <button
              type="button"
              className="drawola-studio__import"
              onClick={() => importInputRef.current?.click()}
              disabled={busy || !ready}
              title={t("studio.import.hint")}
              aria-label={t("studio.import.aria")}
            >
              {ImportIcon}
              {/* Hidden below 480px (see studio.scss) — the button collapses
                  to icon-only there; `title` + `aria-label` keep it named. */}
              <span className="drawola-studio__import-label">
                {t("studio.import.label")}
              </span>
            </button>
            <input
              ref={importInputRef}
              className="drawola-studio__import-input"
              type="file"
              accept={IMPORT_ACCEPT}
              tabIndex={-1}
              aria-hidden="true"
              onChange={(event) => {
                const input = event.currentTarget;
                const file = input.files?.[0] ?? null;
                // Clear before handling: `input.files` is cleared with it, so
                // the File reference has to be taken first. Resetting is what
                // lets the user re-pick the SAME file after a failed import —
                // otherwise `change` never fires a second time.
                input.value = "";
                handleImportFile(file);
              }}
            />
          </div>
        </header>

        <main className="drawola-studio__content">
          {errorAlert}

          <section className="drawola-studio__section">
            <h2 className="drawola-studio__section-title">
              {t("studio.create.title")}
            </h2>
            <div className="drawola-studio__create-strip">
              <button
                type="button"
                className="drawola-studio__new-card"
                onClick={handleCreate}
                disabled={busy || !ready}
              >
                <span className="drawola-studio__new-card-plus" aria-hidden>
                  +
                </span>
                <span className="drawola-studio__new-card-label">
                  {t("studio.create.blank")}
                </span>
                <span className="drawola-studio__new-card-hint">
                  {t("studio.create.blankHint")}
                </span>
              </button>
              {STUDIO_TEMPLATES.map((template) => {
                const name = t(template.nameKey);
                const description = t(template.descriptionKey);
                // `undefined` = still rendering, `null` = nothing to show;
                // both fall back to the neutral placeholder.
                const preview = templatePreviews[template.id];
                return (
                  <button
                    key={template.id}
                    type="button"
                    className="drawola-studio__template-card"
                    title={description}
                    aria-label={t("studio.templates.useAria", { name })}
                    onClick={() => handleCreateFromTemplate(template)}
                    disabled={busy || !ready}
                  >
                    <span className="drawola-studio__template-preview">
                      {preview ? (
                        <img
                          className="drawola-studio__template-preview-img"
                          src={preview}
                          alt=""
                        />
                      ) : (
                        <span
                          className={clsx(
                            "drawola-studio__template-placeholder",
                            { "is-loading": preview === undefined },
                          )}
                          aria-hidden="true"
                        >
                          {SketchIcon}
                        </span>
                      )}
                    </span>
                    <span className="drawola-studio__template-text">
                      <span className="drawola-studio__template-name">
                        {name}
                      </span>
                      <span className="drawola-studio__template-hint">
                        {description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="drawola-studio__section">
            <h2 className="drawola-studio__section-title">
              {t("studio.myDraws")}
              {visibleDocs && visibleDocs.length > 0 && (
                <span className="drawola-studio__count">
                  {visibleDocs.length}
                </span>
              )}
            </h2>

            {visibleDocs === null && (
              <p className="drawola-studio__muted">{t("files.loading")}</p>
            )}

            {emptyLibrary && (
              <div className="drawola-studio__empty">
                <span className="drawola-studio__empty-art" aria-hidden="true">
                  {SketchIcon}
                </span>
                <h3>{t("studio.empty.title")}</h3>
                <p>{t("studio.empty.body")}</p>
                <button
                  type="button"
                  className="drawola-studio__cta"
                  onClick={handleCreate}
                  disabled={busy || !ready}
                >
                  {t("studio.empty.cta")}
                </button>
              </div>
            )}

            {noSearchResults && (
              <p className="drawola-studio__muted">
                {t("studio.noResults", { query: query.trim() })}
              </p>
            )}

            {visibleDocs && visibleDocs.length > 0 && layout === "grid" && (
              <ul className="drawola-studio__grid">
                {visibleDocs.map(({ doc, snippet }) => (
                  <li key={doc.id} className="drawola-studio__card">
                    <button
                      type="button"
                      className="drawola-studio__thumb"
                      onClick={() => handleOpen(doc.id)}
                      aria-label={t("studio.openAria", { name: doc.name })}
                    >
                      {renderThumbnail(doc)}
                    </button>
                    <div className="drawola-studio__card-foot">
                      <div className="drawola-studio__card-text">
                        {renderName(doc)}
                        {renderSnippet(snippet)}
                        <span className="drawola-studio__meta">
                          {formatRelativeTime(doc.updatedAt, t)}
                          <SyncStatusIcon
                            status={
                              cloudSync
                                ? cloudSync.statusOf(doc)
                                : { kind: "local" }
                            }
                            providerName={cloudSync?.providerName(doc) ?? ""}
                            defaultProviderName={defaultProviderName}
                            theme={theme}
                            onRepair={
                              cloudSync
                                ? () => cloudSync.openRepair(doc.id)
                                : undefined
                            }
                          />
                          {hasLiveShare(doc) && (
                            <span
                              className="drawola-studio__badge drawola-studio__badge--shares"
                              title={t("studio.sharesBadge")}
                            >
                              {t("studio.sharesBadge")}
                            </span>
                          )}
                        </span>
                      </div>
                      {renderMenu(doc)}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {visibleDocs && visibleDocs.length > 0 && layout === "list" && (
              <ul className="drawola-studio__list">
                {visibleDocs.map(({ doc, snippet }) => (
                  <li key={doc.id} className="drawola-studio__row">
                    <button
                      type="button"
                      className="drawola-studio__row-open"
                      onClick={() => handleOpen(doc.id)}
                      aria-label={t("studio.openAria", { name: doc.name })}
                    >
                      <span className="drawola-studio__row-icon" aria-hidden>
                        {SketchIcon}
                      </span>
                    </button>
                    <div className="drawola-studio__row-name">
                      {renderName(doc)}
                      {renderSnippet(snippet)}
                    </div>
                    <span className="drawola-studio__row-cell">
                      {formatRelativeTime(doc.updatedAt, t)}
                    </span>
                    <span className="drawola-studio__row-cell">
                      {formatMB(doc.sizeBytes ?? 0)}
                    </span>
                    <span className="drawola-studio__row-cell">
                      <SyncStatusIcon
                        status={
                          cloudSync
                            ? cloudSync.statusOf(doc)
                            : { kind: "local" }
                        }
                        providerName={cloudSync?.providerName(doc) ?? ""}
                        defaultProviderName={defaultProviderName}
                        theme={theme}
                        onRepair={
                          cloudSync
                            ? () => cloudSync.openRepair(doc.id)
                            : undefined
                        }
                      />
                      {hasLiveShare(doc) && (
                        <span
                          className="drawola-studio__badge drawola-studio__badge--shares"
                          title={t("studio.sharesBadge")}
                        >
                          {t("studio.sharesBadge")}
                        </span>
                      )}
                    </span>
                    {renderMenu(doc)}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <PrivacyNotice />
          <footer className="drawola-studio__build">
            <AppLanguageSelect
              className="drawola-studio__language"
              label={t("studio.language")}
            />
            {guideHref && (
              <>
                <a href={guideHref} target="_blank" rel="noopener noreferrer">
                  {t("studio.welcome.manual")}
                </a>
                {" · "}
              </>
            )}
            Support &amp; feature requests:{" "}
            <a href="mailto:drawola.app@gmail.com">drawola.app@gmail.com</a>
            {" · "}build{" "}
            {import.meta.env.VITE_APP_GIT_SHA?.slice(0, 7) || "dev"}
          </footer>
        </main>
      </div>

      {pendingDelete && (
        <StudioConfirmDialog
          title={t("studio.menu.delete")}
          body={
            (pendingDelete.shares?.length ?? 0) > 0
              ? `${t("files.deleteConfirm", {
                  name: pendingDelete.name,
                })} ${t("studio.delete.revokeNotice")}`
              : t("files.deleteConfirm", { name: pendingDelete.name })
          }
          confirmLabel={t("studio.menu.delete")}
          cancelLabel={t("cloud.cancel")}
          danger
          checkbox={
            pendingDelete.cloud && cloudSync
              ? {
                  label: t("cloud.sync.deleteRemoteCheckbox", {
                    provider: cloudSync.providerName(pendingDelete),
                  }),
                  checked: deleteCloudCopy,
                  disabled: !cloudSync.engine.canDeleteRemote(pendingDelete),
                  hint: cloudSync.engine.canDeleteRemote(pendingDelete)
                    ? undefined
                    : cloudSync.engine.isSignedIn(
                        pendingDelete.cloud.providerId,
                      )
                    ? t("cloud.sync.deleteRemoteOffline", {
                        provider: cloudSync.providerName(pendingDelete),
                      })
                    : t("cloud.sync.deleteRemoteNeedsSignIn", {
                        provider: cloudSync.providerName(pendingDelete),
                      }),
                  onChange: setDeleteCloudCopy,
                }
              : undefined
          }
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setPendingDelete(null)}
        />
      )}
      {sharesDoc && onRevokeShare && (
        <StudioSharesDialog
          docName={sharesDoc.name}
          shares={sharesDoc.shares ?? []}
          onRevokeShare={async (shareId) => {
            await onRevokeShare(sharesDoc.id, shareId);
          }}
          onClose={() => setSharesDocId(null)}
        />
      )}
    </div>
  );
};
